import React from 'react';
import {Dimensions, Platform, View} from 'react-native';
import {Card, Image, makeStyles} from '@rn-vui/themed';
import {observer} from 'mobx-react';

import onLight from '../../../images/brand/rewards/on-light.png';
import {AppRoute} from '../../../models/AppRoute';
import useAppNavigation from '../../../navigation/useAppNavigation';
import {useStores} from '../../../stores/use-stores';
import {OffersItemWithMflId} from '../../../transport/types';
import Text from '../../themed/Text';
import PressableWithOpacityFeedback from '../../ui-controls/PressableWithOpacityFeedback';

const ExclusiveOffersTileCompact = observer((offer: OffersItemWithMflId) => {
  const styles = useStyles();
  const navigation = useAppNavigation();
  const {cartStore} = useStores();

  const isApplied = cartStore.isOfferApplied(offer.mflId);

  const handleApply = () => {
    cartStore.applyOffer(offer.mflId);
  };

  const handleRemove = () => {
    cartStore.removeActivatedDeal();
  };

  return (
    <PressableWithOpacityFeedback
      onPress={() => {
        if (offer.mflId) {
          navigation.navigate(AppRoute.EXCLUSIVE_OFFER_DETAIL, {
            mflId: offer?.mflId,
          });
        }
      }}>
      <Card
        noMargin
        applied={null}
        variant={'button2'}
        containerStyle={[
          {
            height: 203,
            maxWidth: 270,
            width: Dimensions.get('window').width - 30,
          },
          isApplied && styles.appliedCard,
        ]}
        wrapperStyle={{
          flex: 1,
          gap: 12,
        }}>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
          <Image source={onLight} style={{width: 50, height: 36}} />
          <Text
            style={styles.title}
            numberOfLines={2}
            ellipsizeMode="tail"
            textBreakStrategy="simple"
            lineBreakStrategyIOS="none">
            {offer.tileHeadline}
          </Text>
        </View>
        <Card.Divider
          style={{
            marginBottom: 0,
            borderStyle: Platform.select({web: 'dashed'}),
          }}
        />
        <Text
          style={styles.description}
          numberOfLines={2}
          ellipsizeMode="tail"
          textBreakStrategy="simple"
          lineBreakStrategyIOS="none">
          {offer.description}
        </Text>
        <View style={{flex: 1}} />
        {isApplied ? (
          <PressableWithOpacityFeedback
            onPress={e => {
              e?.stopPropagation();
              handleRemove();
            }}
            disabled={cartStore.dirtyOrder}
            style={cartStore.dirtyOrder ? {opacity: 0.5} : undefined}
            testID="exclusive-offer-remove-button">
            <Text style={[styles.apply, styles.remove]} variant="heading3">
              REMOVE
            </Text>
          </PressableWithOpacityFeedback>
        ) : (
          <PressableWithOpacityFeedback
            onPress={e => {
              e?.stopPropagation();
              handleApply();
            }}
            disabled={cartStore.dirtyOrder}
            style={cartStore.dirtyOrder ? {opacity: 0.5} : undefined}
            testID="exclusive-offer-apply-button">
            <Text style={styles.apply} variant="heading3">
              APPLY
            </Text>
          </PressableWithOpacityFeedback>
        )}
      </Card>
    </PressableWithOpacityFeedback>
  );
});

const useStyles = makeStyles(theme => {
  return {
    title: {
      flex: 1,
      fontSize: 14,
      fontWeight: 'bold',
      userSelect: 'none',
    },
    description: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.grey2,
      userSelect: 'none',
    },
    apply: {
      textAlign: 'right',
    },
    applied: {
      color: theme.colors.black,
    },
    remove: {
      color: '#DC2626',
    },
    appliedCard: {
      borderColor: '#F59E0B',
      borderWidth: 2,
    },
  };
});

export default ExclusiveOffersTileCompact;
